import React, { useRef, useState } from "react";
import { AxiosError } from "axios";
import { Box, Button } from "@mui/material";
import { BasicButton, COLORS, Dialog, LazyItem, NoResultFound, TYPOGRAPHY_COMMON_STYLES, DeleteButton, DeleteDialog } from "components";
import { AttachedDocIcon, ClipboardIcon } from "assets";
import { useNotify, useUploadScorecardDocuments, useDeleteScorecardDocument } from "hooks";
import { ScorecardDocument } from "api";

interface AttachedDocsModalProps {
    isDialogOpen: boolean;
    onDialogClose: () => void;
    dataId: number;
    documents: ScorecardDocument[];
    onUploadSuccess?: () => void;
    onDocumentDelete?: () => void;
}

export const AttachedDocsModal: React.FC<AttachedDocsModalProps> = ({
    isDialogOpen,
    onDialogClose,
    dataId,
    documents,
    onUploadSuccess,
    onDocumentDelete,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<ScorecardDocument | null>(null);
    const uploadDocumentsMutation = useUploadScorecardDocuments();
    const deleteDocumentMutation = useDeleteScorecardDocument();
    const notify = useNotify();

    const handleDocumentClick = (document: ScorecardDocument) => {
        if (document.downloadUrl) {
            window.open(document.downloadUrl, '_blank');
        }
    };

    const handleDeleteDocument = (document: ScorecardDocument, event: React.MouseEvent) => {
        event.stopPropagation();
        setDocumentToDelete(document);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            await deleteDocumentMutation.mutateAsync(documentToDelete.id);
            onDocumentDelete?.();
            handleCloseDeleteDialog();
        } catch (error) {
            // Error is already handled by the hook
        }
    };

    const handleAddNewDocument = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            setSelectedFile(files[0]);
        }
    };

    const handleRemoveSelectedFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        if (!selectedFile) return;

        try {
            setIsLoading(true);
            await uploadDocumentsMutation.mutateAsync({ dataId, files: [selectedFile] });
            setSelectedFile(null);
            onUploadSuccess?.();
            onDialogClose();
        } catch (error) {
            const axiosError = error as AxiosError;
            notify.error(typeof axiosError?.response?.data === 'string' ? axiosError.response.data : 'Failed to upload document. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onDialogClose();
    };

    return (
        <>
            <Dialog isOpen={isDialogOpen} dividers onClose={handleClose} title={<div />}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ color: COLORS.Mainblue, fontSize: '20px', fontWeight: '500' }}>
                        Attached Documents
                    </div>

                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <BasicButton
                            variant="outlined"
                            sx={{ marginTop: '0px', marginRight: '0px', fontWeight: '500', width: "240px" }}
                            startIcon={<ClipboardIcon style={{ width: "16px", height: "16px", color: "#054254" }} />}
                            onClick={handleAddNewDocument}
                        >
                            Add Supporting Document
                        </BasicButton>

                        {selectedFile && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "12px", color: "#054254" }}>
                                    {selectedFile.name}
                                </span>
                                <Button
                                    onClick={handleRemoveSelectedFile}
                                    sx={{
                                        minWidth: "auto",
                                        width: "20px",
                                        height: "20px",
                                        padding: 0,
                                        color: "#666",
                                        "&:hover": { color: "#333" }
                                    }}
                                >
                                    ×
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        width: "488px",
                        height: "264px",
                        flexShrink: 0,
                        borderRadius: "10px",
                        border: "1px solid #A9A9A9",
                        overflow: "auto",
                        padding: "0px 10px",
                    }}>
                        {documents.length === 0 ? (
                            <Box sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                flexDirection: "column",
                                backgroundColor: "transparent",
                            }}>
                                <NoResultFound heading="No Documents Yet" body="" sx={{ color: "#A9A9A9", backgroundColor: "transparent" }} />
                            </Box>
                        ) : (
                            <>
                                {documents.map((document, index) => (
                                    <LazyItem key={document.id}>
                                        <div>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                style={{
                                                    color: COLORS.Romance,
                                                    fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                                    fontSize: 14,
                                                    lineHeight: "18px",
                                                    backgroundColor: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "8px 12px",
                                                    margin: "2px 0",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleDocumentClick(document)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleDocumentClick(document);
                                                    }
                                                }}
                                            >
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <AttachedDocIcon
                                                        style={{
                                                            marginRight: 10,
                                                            marginTop: 0,
                                                            marginBottom: 0,
                                                            marginLeft: 0,
                                                            width: "20px",
                                                            height: "20px",
                                                            color: "#054254",
                                                        }}
                                                    />
                                                    <p style={{
                                                        color: "black",
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                    }}>
                                                        {document.filename}
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={(e) => handleDeleteDocument(document, e)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            handleDeleteDocument(document, e as any);
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <DeleteButton
                                                        onClick={() => { }} // Empty function since we handle click on the wrapper div
                                                        size="small"
                                                        width="24px"
                                                        height="24px"
                                                        iconColor="#666"
                                                        tooltipTitle="Delete document"
                                                        aria-label={`Delete ${document.filename}`}
                                                    />
                                                </div>
                                            </div>
                                            {index < documents.length - 1 && (
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        height: "1px",
                                                        opacity: 0.5,
                                                        background: "#8CA0A8",
                                                        padding: "0 10px",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </LazyItem>
                                ))}
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "20px" }}>
                        <Button onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isLoading || !selectedFile}
                            variant="contained"
                            sx={{
                                backgroundColor: "#054254",
                                "&:hover": { backgroundColor: "#043a47" },
                                "&:disabled": { backgroundColor: "#ccc" }
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {deleteDialogOpen && documentToDelete && (
                <DeleteDialog
                    isOpen={deleteDialogOpen}
                    onCancel={handleCloseDeleteDialog}
                    onDeleteClick={handleConfirmDelete}
                    target="document"
                    title="Delete document?"
                    confirmButtonDisabled={deleteDocumentMutation.isLoading}
                    confirmTitle={deleteDocumentMutation.isLoading ? "Deleting..." : "Delete"}
                />
            )}

            <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png"
            />
        </>
    );
};
